import { useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'

import { currencyString } from '@sales-center/common'
import { DiscountType } from '@user/store/leadOppProducts'

import { ILeadOppProductFormModel, subTotal, tax, total } from '../typings'

export function useCalculateTotals(isCanadianTax: boolean) {
  const { values, setValues, setFieldValue } =
    useFormikContext<ILeadOppProductFormModel>()

  const {
    initialPrice,
    initialDiscountAmount,
    initialDiscountType,

    subtractOne,
    recurringPrice,
    recurringDiscountAmount,
    recurringDiscountType,

    taxRate,
    chargeTax,

    quantity,
    annualOccurrences,
    gstHstRate,
    pstQstRate,
  } = values

  const initialSubTotal = useMemo(
    () => subTotal(initialPrice, initialDiscountAmount, initialDiscountType),
    [initialDiscountAmount, initialDiscountType, initialPrice],
  )

  const initialTax = useMemo(
    () => tax(initialSubTotal, taxRate),
    [initialSubTotal, taxRate],
  )

  // Canadian Tax calculation for GST & PST
  const initialGSTTax = useMemo(
    () => tax(initialSubTotal, gstHstRate),
    [initialSubTotal, gstHstRate],
  )

  const initialPSTTax = useMemo(
    () => tax(initialSubTotal, pstQstRate),
    [initialSubTotal, pstQstRate],
  )

  const canTaxTotal = initialGSTTax + initialPSTTax

  const initialTotal = useMemo(
    () =>
      total(
        initialSubTotal,
        isCanadianTax ? canTaxTotal : initialTax,
        chargeTax,
      ),
    [canTaxTotal, chargeTax, initialSubTotal, initialTax, isCanadianTax],
  )

  const initialTotalNoTax = useMemo(
    () =>
      total(initialSubTotal, isCanadianTax ? canTaxTotal : initialTax, false),
    [canTaxTotal, initialSubTotal, initialTax, isCanadianTax],
  )

  const initialTotalLabel = useMemo(
    () => `$${currencyString(isNaN(initialTotal) ? 0 : initialTotal)}`,
    [initialTotal],
  )
  //#endregion

  //#region recurring calculations
  const recurringSubTotal = useMemo(
    () =>
      subTotal(recurringPrice, recurringDiscountAmount, recurringDiscountType),
    [recurringDiscountAmount, recurringDiscountType, recurringPrice],
  )

  const recurringTax = useMemo(
    () => tax(recurringSubTotal, taxRate),
    [recurringSubTotal, taxRate],
  )

  // Canadian Tax calculation for GST & PST
  const recurringGSTTax = useMemo(
    () => tax(recurringSubTotal, gstHstRate),
    [recurringSubTotal, gstHstRate],
  )

  const recurringPSTTax = useMemo(
    () => tax(recurringSubTotal, pstQstRate),
    [recurringSubTotal, pstQstRate],
  )

  const canRecurringTaxTotal = recurringGSTTax + recurringPSTTax

  const recurringTotal = useMemo(
    () =>
      total(
        recurringSubTotal,
        isCanadianTax ? canRecurringTaxTotal : recurringTax,
        chargeTax,
      ),
    [
      canRecurringTaxTotal,
      chargeTax,
      isCanadianTax,
      recurringSubTotal,
      recurringTax,
    ],
  )

  const recurringTotalNoTax = useMemo(
    () =>
      total(
        recurringSubTotal,
        isCanadianTax ? canRecurringTaxTotal : recurringTax,
        false,
      ),
    [canRecurringTaxTotal, isCanadianTax, recurringSubTotal, recurringTax],
  )

  const recurringTotalLabel = useMemo(() => {
    const qty = quantity > 0 ? quantity : 1
    return `$${currencyString(recurringTotal * qty)}`
  }, [quantity, recurringTotal])
  //#endregion

  //#region effects
  // update subtract one based on annual occurrences
  useEffect(() => {
    if (annualOccurrences === 0) setFieldValue('subtractOne', false)
  }, [annualOccurrences, setFieldValue])

  // update discount type based on value
  useEffect(() => {
    const type: DiscountType = initialDiscountAmount.includes('%')
      ? 'percentage'
      : 'dollar'
    setFieldValue('initialDiscountType', type)
  }, [initialDiscountAmount, setFieldValue])

  // update discount type based on value
  useEffect(() => {
    const type: DiscountType = recurringDiscountAmount.includes('%')
      ? 'percentage'
      : 'dollar'
    setFieldValue('recurringDiscountType', type)
  }, [recurringDiscountAmount, setFieldValue])

  useEffect(() => {
    const qty = quantity < 1 ? 1 : quantity
    const ao = subtractOne ? annualOccurrences - 1 : annualOccurrences
    const totalFirst = (initialTotalNoTax + recurringTotalNoTax * ao) * qty
    const totalSubsequent = recurringTotalNoTax * annualOccurrences * qty

    setValues((values) => ({ ...values, totalFirst, totalSubsequent }))
  }, [
    annualOccurrences,
    initialTotalNoTax,
    quantity,
    recurringTotalNoTax,
    setValues,
    subtractOne,
  ])
  //#endregion

  return {
    initialTax,
    initialTotalNoTax,
    initialTotalLabel,

    recurringTax,
    recurringTotalNoTax,
    recurringTotalLabel,

    initialGSTTax,
    initialPSTTax,
    recurringGSTTax,
    recurringPSTTax,
  }
}
